import { AfterViewInit, ChangeDetectorRef, Directive, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ApicallService } from "@core-services/apicall.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { InfoService } from "@core-services/info.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { round } from "@helpers/utils";
import { FiatService } from "@main-services/currency";
import { UserService } from "@main-services/user.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { Fee, Fiat, FiatWallet, Field, Limit, PayoutAccount, Provider, User } from "@models";
import { VerifyPayeeMode, VerifyPayeePayload, VerifyPayeeResponse, VerifyPayeeResult } from "@models/verify-payee";
import { CustomModal } from "@modules/core/components/base-modals";
import { finalize, Observable, take } from "rxjs";
import { StepSummaryModal } from "../step-summary.modal";
import { VerifyPayeeService } from "./services/verify-payee.service";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";

@Directive()
export abstract class FiatTransactionModal extends StepSummaryModal implements AfterViewInit {
	public readonly verifyPayeeService = inject(VerifyPayeeService);
	public readonly statsigService = inject(StatsigClientService);
	public limit: Limit = new Limit();
	protected category: string = "fiat_transaction";
	public pinSent = false;
	public selectedAccount = signal<PayoutAccount>(null);
	public showLoadingSpinner = this.verifyPayeeService.showLoadingSpinner;
	public constructor(protected confirmModalService: ConfirmModalService) {
		super(confirmModalService);
	}
	public isBankTransfer = this.verifyPayeeService.isBankTransfer;
	protected readonly cdr = inject(ChangeDetectorRef);

	public ngAfterViewInit(): void {
		super.ngAfterViewInit();
		this.data.transaction.setUserWantsType("gross");
		this.statsigService
			.isFeatureActive(FEAT_GATES.PAYMENTS_VOP)
			.pipe(take(1))
			.subscribe((isVopEnabled) => this.verifyPayeeService.isVopFlagEnabled.set(isVopEnabled));
	}

	public save(): void {
		if (this.isMax()) {
			super.finish();
		}
	}

	public switch(): void {
		this.data.transaction.switchUserWants();
		this.userWants();
	}

	public setSummary(): void {
		if (!this.goToSummaryAllowed()) {
			const key = this.label === "withdrawal" ? "withdraw" : this.label; // Quickfix to solve label and key difference
			this.infoService.setError(
				this.translationService.translate("min_amount_" + key + "_AMOUNT", [
					this.minTransactionAmount() + " " + this.fiat.getSymbol()
				])
			);
			return;
		}
		this.checkedSufficientFunds();
	}

	protected checkedSufficientFunds(): void {
		this.moveToSummary();
	}

	protected moveToSummary(): void {
		const isVopFlagEnabled = this.verifyPayeeService.isVopFlagEnabled();
		const isSendMode = this.verifyPayeeService.verifyPayeeMode() === VerifyPayeeMode.SEND;
		const shouldVerifyPayee = isVopFlagEnabled && (isSendMode || this.isBankTransfer());
		if (shouldVerifyPayee) {
			this.showLoadingSpinner.set(true);
			this.verifyPayee$()
				.pipe(
					take(1),
					finalize(() => this.showLoadingSpinner.set(false))
				)
				.subscribe({
					next: (verifyPayeeResponse: VerifyPayeeResponse) => {
						this.verifyPayeeService.verifyPayeeResponse.set(verifyPayeeResponse);
						this.moveToSummaryMain();
					},
					error: () => {
						this.verifyPayeeService.verifyPayeeResponse.set({
							result: VerifyPayeeResult.ERROR
						} as VerifyPayeeResponse);
						this.moveToSummaryMain();
					}
				});
		} else {
			this.moveToSummaryMain();
		}
	}

	public minTransactionAmount(): number {
		if (this.limit && this.limit.getMin() < Number.MAX_VALUE) {
			return this.limit.getMin();
		}
		return 0;
	}

	public goToSummaryAllowed(): boolean {
		return this.isSufficient() && !this.amountOverMax();
	}

	public amountOverMax(): boolean {
		return this.data.transaction.total > this.maxAmount;
	}

	public isSufficient(): boolean {
		return this.amount >= this.minTransactionAmount();
	}

	public checkNegative(amount: number): number {
		return Math.max(round(amount, this.fiat.getPrecision()), 0);
	}

	public userWants(): void {
		this.data.transaction.setFiatId(this.fiat.getId());

		if (this.amount < this.limit.getMin()) {
			this.data.transaction.setAmount(Math.max(0, this.data.transaction.getAmount()));
			return;
		}
		this.isMax();
	}

	public isMax(): boolean {
		if (this.amount > this.maxAmount && this.maxAmount > 0) {
			let info = "FIRST_is_higher_than_your_limit_SECOND";
			info = this.translationService.translate(info, [this.amount + "", this.maxAmount + ""]);
			this.infoService.setInfo(info);
			return false;
		}
	}

	public get apicallService(): ApicallService {
		return CustomModal.injector.get(ApicallService);
	}

	public get fiatwalletService(): FiatWalletService {
		return CustomModal.injector.get(FiatWalletService);
	}

	public get walletService(): TradableWalletListService {
		return CustomModal.injector.get(TradableWalletListService);
	}

	public get masterdataService(): MasterdataService {
		return CustomModal.injector.get(MasterdataService);
	}

	public get fiatService(): FiatService {
		return CustomModal.injector.get(FiatService);
	}

	public get infoService(): InfoService {
		return CustomModal.injector.get(InfoService);
	}

	public get userService(): UserService {
		return CustomModal.injector.get(UserService);
	}

	public get user(): User {
		return this.userService.user;
	}

	public get amount(): number {
		let a = this.data.transaction.getAmount();
		if (isNaN(a)) {
			a = 0;
		}
		return a;
	}

	private providerFees(): { fixedFee: number; maxFee: number; percentFee: number } {
		const providerFee: Fee = this.provider.getFees().find((f: Fee) => f.getFiatId() === this.fiat.getId());
		const fixedFee: number = providerFee.getFeeFix();
		const maxFee: number = providerFee.getFeeMax();
		const percentFee: number = providerFee.getFeePercent();

		return { fixedFee, maxFee, percentFee };
	}

	protected getFee(providedValue: number): number {
		const { fixedFee, maxFee, percentFee } = this.providerFees();
		const amount: number = this.data.transaction.areFeesIncluded ? providedValue : providedValue + fixedFee;
		const factor: number = this.data.transaction.areFeesIncluded
			? percentFee / (percentFee + 100)
			: percentFee / 100;
		const fee: number = amount * factor + fixedFee;
		const calculatedFee: number = maxFee > 0.0 ? Math.min(fee, maxFee) : fee;

		return this.roundUp(calculatedFee, this.fiat.getPrecision());
	}

	private roundUp(amount: number, precision: number): number {
		const factor: number = Math.pow(10, precision) * precision;
		if (precision < 0) {
			return factor * Math.ceil(amount / factor);
		}
		return Math.ceil(amount * factor) / factor;
	}

	public max(): void {
		const max: number = this.maxAmount;
		if (this.data.transaction.getAmount() !== max) {
			this.data.transaction.setAmount(max);
			this.userWants();
		} else {
			this.infoService.setInfo(this.translationService.translate("you_already_have_max_amount"));
		}
	}

	public setAmount(amount: number): void {
		this.data.transaction.setAmount(+amount);
		this.userWants();
		this.setSummary();
	}

	public abstract set provider(p: Provider);
	public abstract get provider(): Provider;

	public abstract get fiat(): Fiat;

	public abstract get wallets(): FiatWallet[];

	public abstract get maxAmount(): number;

	public get amounts(): number[] {
		if (this.limit.getDefaults().length > 0) {
			return this.limit.getDefaults().slice(0, 2);
		}
		return [];
	}

	public getStepsFromOfferFiat(): number {
		return Math.min(this.fiatService.stepSizeByItemId(this.fiat.getId()), 0.1);
	}

	public get router(): Router {
		return CustomModal.injector.get(Router);
	}

	public close(abort: boolean = true): void {
		super.close(abort);
	}

	private verifyPayee$(): Observable<VerifyPayeeResponse> {
		const account: PayoutAccount = this.selectedAccount();
		const iban: string = account
			.getFields()
			.find((field: Field) => field.getName() === "iban")
			.getValue();
		const name: string = account
			.getFields()
			.find((field: Field) => field.getName() === "payout_receiver_name")
			.getValue();
		const currency: string = this.fiat.getSymbol();
		const payload: VerifyPayeePayload = {
			iban,
			name,
			currency
		};
		return this.verifyPayeeService.verifyPayee$(payload);
	}

	private moveToSummaryMain(): void {
		if (this.isMax()) {
			this.summary = this.isSufficient() && this.isMax();
			this.trackSelect("amount");
		}
		this.doTrapFocusTime();
		this.cdr.markForCheck();
	}
}
